import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InputParameter, InputParameterSchema, KeyValueSchema } from "./constants";
import { z } from "zod";
import { Privacy } from "@/helpers/constants";

export const ConfigVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: z.array(InputParameterSchema),
  /** Absent or null: the integration's own flag applies. */
  supportsSaasConnectionTest: z.boolean().nullable().optional(),
});

export const DatamateIntegrationSchema = z.object({
  name: z.string(),
  id: z.coerce.string(),
  type: z.string(),
  description: z.string(),
  config: z.array(InputParameterSchema).optional(),
  additionalConfigVariants: z.array(ConfigVariantSchema).optional(),
  tools: z
    .array(
      z.object({
        name: z.string(),
        key: z.string(),
        enable_all: z.array(z.string()).nullable().optional(),
      })
    )
    .optional(),
  toolConfig: z.array(InputParameterSchema).or(z.array(KeyValueSchema)).optional(),
  supportsSaasConnectionTest: z.boolean().default(false),
  supportsLocalConnectionTest: z.boolean().default(false),
  /** Whether a workspace chooses one connection for this integration (the
   * backend's catalog flag). False for integrations whose tools take the
   * connection per call (warehouses), custom code, and those with no config. */
  workspace_connection: z.boolean(),
});

export type DatamateIntegration = z.infer<typeof DatamateIntegrationSchema>;

export interface CommonIntegrationInstance extends DatamateIntegration {
  inputs?: InputParameter[];
}

export const CommonIntegrationInstanceSchema = DatamateIntegrationSchema.extend({
  inputs: z.array(InputParameterSchema).optional(),
});

export const TeammateSelectedIntegrationSchema = z.object({
  id: z.coerce.string(),
  tools: z.array(
    z.object({
      key: z.string(),
    })
  ),
  /** The tenant connection this workspace uses for the integration; unset
   * means each caller falls back to their own default connection. When
   * `connection_ids` is present it is one of them. */
  connection_id: z.coerce.string().nullable().optional(),
  /** The owner's connections enabled in this workspace. Absent means all of
   * them are; an empty list means none is. It must survive a round trip: an
   * edit that leaves it out keeps the stored list, so dropping it here would
   * hide it, and sending a stale one back could be rejected. */
  connection_ids: z.array(z.coerce.string()).nullable().optional(),
});

export type TeammateSelectedIntegration = z.infer<typeof TeammateSelectedIntegrationSchema>;

export const TeammateSchema = z.object({
  id: z.coerce.string().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  integrations: z.array(TeammateSelectedIntegrationSchema).optional(),
  interfaces: z.array(z.string()).optional(),
  memory_enabled: z.boolean().optional(),
  knowledge_engine_enabled: z.boolean().optional(),
  knowledge_bases: z.array(z.number()).optional().nullable(),
  // How many library skills are attached, served with the list so a page of
  // workspaces costs one request rather than one `GET /skills` per row.
  // Optional because a backend that predates it sends nothing, and a card
  // must drop the segment rather than claim "0 skills".
  skill_count: z.number().optional(),
  // Includes RESTRICTED so a shared workspace parses. Without it `safeParse`
  // fails on the whole /datamates/ list, the client's schema-patch cannot
  // repair a ZodEnum, and the raw response passes through unvalidated with two
  // Sentry errors per fetch.
  privacy: z
    .enum([Privacy.PRIVATE, Privacy.PUBLIC, Privacy.RESTRICTED])
    .default(Privacy.PRIVATE)
    .optional(),
  user_id: z.number().optional(),
  // Whether the caller owns the workspace, as the server and the engine judge
  // it. Optional because a backend that predates it sends nothing.
  is_owner: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type Teammate = z.infer<typeof TeammateSchema>;

export const TeammateCreateResponseSchema = z.object({
  id: z.coerce.number(),
});

export type TeammateCreateResponse = z.infer<typeof TeammateCreateResponseSchema>;

export const TeammateUpdateResponseSchema = z.object({
  datamate: TeammateSchema,
});

export type TeammateUpdateResponse = z.infer<typeof TeammateUpdateResponseSchema>;

export const UsageSchema = z.object({
  totals: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  previous_totals: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  datamates_usage: z.record(
    z.string(),
    z.object({
      assists: z.number(),
      guardrails: z.number(),
    })
  ),
});

export type Usage = z.infer<typeof UsageSchema>;

export interface MCPStateProps {
  teammate: Teammate | null;
}

/** The outcome of a live connection test. Returned by the test, never stored. */
export enum ConnectionTestStatus {
  Passed = "passed",
  Failed = "failed",
  NotTestable = "not_testable",
}

export const ConnectionTestResultSchema = z.object({
  // An unknown future status reads as absent, not a parse failure.
  test_status: z.nativeEnum(ConnectionTestStatus).optional().catch(undefined),
  test_message: z.string().nullish(),
});

export const ConnectionSchema = z.object({
  id: z.coerce.string().optional(),
  name: z.string(),
  integration_id: z.string(),
  config: z.record(z.string(), z.any()),
  /** Owner-only field names whose stored secrets could not be decrypted. */
  unreadable_secret_fields: z.array(z.string()).optional(),
  created_at: z.string().optional(),
  updated_at: z.string().nullable(),
  // Sharing. Optional: an older API sends none of these, and a
  // connection without them is the caller's own and private.
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC, Privacy.RESTRICTED]).optional(),
  /** False for a connection shared with the caller: usable, not editable, and
   * its config carries no secret fields. */
  is_owner: z.boolean().optional(),
  /** Who shared it; set only when `is_owner` is false. */
  owner: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  /** Who the caller's own `restricted` connection is shared with. */
  shares: z
    .object({ user_ids: z.number().array(), team_ids: z.number().array() })
    .nullable()
    .optional(),
  /** Whether the connection has a live check: false for an integration, or a
   * configuration type (e.g. Snowflake external browser), without one. */
  testable: z.boolean().optional(),
});

export type Connection = z.infer<typeof ConnectionSchema>;

/** The caller's own connection — anything not explicitly shared with them. */
export const isOwnConnection = (connection: Pick<Connection, "is_owner">) =>
  connection.is_owner !== false;
export type ConfigVariant = z.infer<typeof ConfigVariantSchema>;

export const initialState: MCPStateProps = {
  teammate: null,
};

const mcpSlice = createSlice({
  name: "mcpState",
  initialState,
  reducers: {
    setTeammate: (state, action: PayloadAction<Teammate>) => {
      state.teammate = action.payload;
    },
    updateTeammate: (state, action: PayloadAction<Partial<Teammate>>) => {
      state.teammate = { ...state.teammate, ...action.payload } as Teammate;
    },
  },
});

export const { setTeammate, updateTeammate } = mcpSlice.actions;
export default mcpSlice;

export const DatamateAuditEventModelSchema = z.object({
  id: z.number(),
  event_type: z.string(),
  user_id: z.number(),
  tool_name: z.string(),
  integration_id: z.string(),
  payload: z.any(),
  teammate_id: z.string(),
  timestamp: z.string(),
  created_at: z.string(),
});

export const GetDatamateWithStatsResponseSchema = z.object({
  datamate: TeammateSchema,
  usage: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  events: z.array(DatamateAuditEventModelSchema),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  // Includes RESTRICTED so a shared workspace parses. Without it `safeParse`
  // fails on the whole /datamates/ list, the client's schema-patch cannot
  // repair a ZodEnum, and the raw response passes through unvalidated with two
  // Sentry errors per fetch.
  privacy: z
    .enum([Privacy.PRIVATE, Privacy.PUBLIC, Privacy.RESTRICTED])
    .default(Privacy.PRIVATE)
    .optional(),
  user_id: z.number().optional(),
});

export type DatamateAuditEventModel = z.infer<typeof DatamateAuditEventModelSchema>;
export type GetDatamateWithStatsResponse = z.infer<typeof GetDatamateWithStatsResponseSchema>;

// Aggregation schemas for assist and guardrail endpoints
export const AssistAggregationByIntegrationSchema = z.object({
  integration_type: z.string(),
  assists: z.number(),
});

export const AssistAggregationByToolSchema = z.object({
  tool_call: z.string(),
  assists: z.number(),
});

export const AssistAggregationsResponseSchema = z.array(
  z.union([AssistAggregationByIntegrationSchema, AssistAggregationByToolSchema])
);

export const GuardrailAggregationByIntegrationSchema = z.object({
  integration_type: z.string(),
  guardrails: z.number().optional(),
});

export const GuardrailAggregationByToolSchema = z.object({
  tool_call: z.string(),
  guardrails: z.number().optional(),
});

export const GuardrailAggregationsResponseSchema = z.array(
  z.union([GuardrailAggregationByIntegrationSchema, GuardrailAggregationByToolSchema])
);

export type AssistAggregationByIntegration = z.infer<typeof AssistAggregationByIntegrationSchema>;
export type AssistAggregationByTool = z.infer<typeof AssistAggregationByToolSchema>;
export type AssistAggregationsResponse = z.infer<typeof AssistAggregationsResponseSchema>;
export type GuardrailAggregationByIntegration = z.infer<
  typeof GuardrailAggregationByIntegrationSchema
>;
export type GuardrailAggregationByTool = z.infer<typeof GuardrailAggregationByToolSchema>;
export type GuardrailAggregationsResponse = z.infer<typeof GuardrailAggregationsResponseSchema>;
